import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import ChartContainer from './ChartContainer';
import { api } from '../../api';
import { HourlyMixedDataItem, ChartTooltipProps } from '../../types/dashboard.types';

interface HourlyMixedComparisonChartProps {
  data: HourlyMixedDataItem[];
  loading?: boolean;
}

interface CachedData {
  [date: string]: HourlyMixedDataItem[];
}

function HourlyMixedComparisonChart({
  data: initialData,
  loading: initialLoading = false,
}: HourlyMixedComparisonChartProps) {
  // Estado para controlar a data selecionada (0 = hoje, -1 = ontem, -2 = anteontem, etc.)
  const [dayOffset, setDayOffset] = useState(0);
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(initialLoading);
  const [cache, setCache] = useState<CachedData>({});
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Atualizar dados quando receber novos dados iniciais
  useEffect(() => {
    if (initialData && initialData.length > 0 && dayOffset === 0) {
      setData(initialData);
      // Cachear dados de hoje
      const todayKey = formatDateKey(0);
      setCache(prev => ({ ...prev, [todayKey]: initialData }));
    }
  }, [initialData]);
  
  // Função para formatar a chave de cache da data
  const formatDateKey = (offset: number) => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return date.toISOString().split('T')[0];
  };
  
  // Função para obter o nome do dia
  const getDayName = (offset: number) => {
    if (offset === 0) return 'Hoje';
    if (offset === -1) return 'Ontem';
    
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' });
  };
  
  // Função para buscar dados de uma data específica
  const fetchDataForDate = async (offset: number) => {
    const dateKey = formatDateKey(offset);
    
    // Verificar cache primeiro
    if (cache[dateKey]) {
      setData(cache[dateKey]);
      return;
    }
    
    // Para dias passados, não fazer requisições automáticas
    if (offset < 0) {
      setLoading(true);
      try {
        // Criar a data para consulta
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() + offset);
        const dateStr = targetDate.toISOString().split('T')[0];
        
        // Buscar dados do servidor para a data específica
        const response = await api.getHourlyComparisonForDate(dateStr);
        
        if (response && Array.isArray(response)) {
          setData(response);
          // Cachear os dados
          setCache(prev => ({ ...prev, [dateKey]: response }));
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('Erro ao buscar dados para a data:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Efeito para buscar dados quando mudar o dia
  useEffect(() => {
    fetchDataForDate(dayOffset);
  }, [dayOffset]);
  
  // Calcular o total de interações do dia selecionado
  const totalDayInteractions = useMemo(() => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => {
      const value = typeof item.hoje === 'string' ? parseInt(item.hoje, 10) : (item.hoje || 0);
      return sum + value;
    }, 0);
  }, [data]);
  
  // Função para navegar entre dias
  const navigateDay = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setDayOffset(prev => prev - 1);
    } else if (direction === 'next' && dayOffset < 0) {
      setDayOffset(prev => prev + 1);
    }
  };
  const formatXAxis = (tickItem: number) => {
    return `${tickItem}:00`;
  };

  // Encontrar o valor máximo de interações para ajustar a escala
  const maxInteractions = React.useMemo(() => {
    if (!data || data.length === 0) return 1000;
    
    console.log("Dados brutos do gráfico misto:", data);
    
    // Verificar o máximo entre todos os tipos de dados (hoje, ontem, semana_anterior)
    const allValues = data.flatMap(item => [
      typeof item.hoje === 'number' ? item.hoje : 0,
      typeof item.ontem === 'number' ? item.ontem : 0,
      typeof item.semana_anterior === 'number' ? item.semana_anterior : 0
    ]);
    
    console.log("Valores extraídos do gráfico misto:", allValues);
    
    const max = Math.max(...allValues);
    console.log("Valor máximo encontrado no gráfico misto:", max);
    
    // Forçar um valor maior para testes - REMOVER DEPOIS DE SOLUCIONAR
    return 1000;
    
    // Arredondar para o próximo múltiplo apropriado
    if (max <= 100) return 100;
    if (max <= 500) return Math.ceil(max / 100) * 100;
    if (max <= 1000) return 1000;
    return Math.ceil(max / 1000) * 1000; // Para valores acima de 1000
  }, [data]);

  // Calcular intervalos do eixo Y com base no valor máximo
  const calculateYAxisTicks = () => {
    if (maxInteractions <= 100) return [0, 20, 40, 60, 80, 100];
    if (maxInteractions <= 500) return [0, 100, 200, 300, 400, 500];
    if (maxInteractions <= 1000) return [0, 200, 400, 600, 800, 1000];
    return [0, maxInteractions/5, maxInteractions*2/5, maxInteractions*3/5, maxInteractions*4/5, maxInteractions];
  };

  const formatYAxis = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
    }
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }: ChartTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 p-3 border border-gray-700 rounded shadow-lg">
          <p className="text-sm font-medium text-gray-300">{`${label}:00`}</p>
          {payload.map((entry, index: number) => (
            <p 
              key={`item-${index}`}
              className="text-sm flex items-center"
              style={{ color: entry.color }}
            >
              <span 
                className="w-3 h-3 inline-block rounded-full mr-2" 
                style={{ backgroundColor: entry.color }}
              ></span>
              <span className="font-medium">{entry.name}:</span>{' '}
              {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ChartContainer 
      title={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <span>Comparação de Interações por Hora</span>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400">Total do dia:</span>
              <span className="font-bold text-blue-400">
                {totalDayInteractions.toLocaleString('pt-BR')}
              </span>
              <span className="text-gray-400">interações</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDay('prev')}
              className="p-1 hover:bg-gray-700 rounded transition-colors text-gray-300 hover:text-white"
              title="Dia anterior"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-sm font-medium px-3 py-1 bg-gray-700 rounded text-gray-200">
              {getDayName(dayOffset)}
            </span>
            <button
              onClick={() => navigateDay('next')}
              disabled={dayOffset === 0}
              className={`p-1 rounded transition-colors ${
                dayOffset === 0 
                  ? 'opacity-50 cursor-not-allowed text-gray-500' 
                  : 'hover:bg-gray-700 text-gray-300 hover:text-white'
              }`}
              title="Próximo dia"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      }
      tooltip="Compara o volume de interações por hora entre o dia selecionado, dia anterior e a mesma data da semana anterior."
      fullWidth={true}
    >
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : data && data.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="hora"
              tickFormatter={formatXAxis}
              stroke="#9CA3AF"
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              stroke="#9CA3AF" 
              tick={{ fontSize: 12 }}
              domain={[0, maxInteractions]}
              ticks={calculateYAxisTicks()}
              label={{ 
                value: 'Interações', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: '#9CA3AF', fontSize: 12 }
              }}
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: 5 }}
              iconType="circle"
              formatter={(value) => <span className="text-sm text-gray-300">{value}</span>}
            />
            <ReferenceLine y={0} stroke="#374151" />
            
            {/* Barra para o dia selecionado */}
            <Bar 
              dataKey="hoje"
              name={dayOffset === 0 ? "Hoje" : getDayName(dayOffset)} 
              fill="#3B82F6" 
              radius={[4, 4, 0, 0]}
              barSize={20}
              animationDuration={500}
            />
            
            {/* Linha para o dia anterior */}
            <Line
              type="monotone"
              dataKey="ontem"
              name={dayOffset === 0 ? "Ontem" : "Dia Anterior"}
              stroke="#8B5CF6"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              animationDuration={500}
            />
            
            {/* Linha para semana anterior */}
            <Line
              type="monotone"
              dataKey="semana_anterior"
              name="Semana Anterior"
              stroke="#10B981"
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              animationDuration={500}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex justify-center items-center h-48 text-gray-400">
          Sem dados disponíveis
        </div>
      )}
    </ChartContainer>
  );
}

export default HourlyMixedComparisonChart; 